/**
 * Task-grain Work board; Upcoming and Task history keep their own entities, routes and saved state. Tenant
 * mounts select rows and run the Visit bulk actions on the Visits they sit on, and a row opens the task panel
 * beside the list. The portal block is read-only: a row opens the occurrence dialog (its month of visits, the
 * task, its Visit and Result), and the header downloads the rows as CSV.
 */
import { ActivityEntityName, DomainType, EntityViewDisplay, PortalClientSecurityRight, SurfaceType, WorkTaskScheduleRow } from '@jms/model';
import { clientPaths } from '@jms/tenant/module.jms.authorisation';
import { RightsService } from '@jms/utils/util.permissions';
import { EntityManifest, EntityManifestSurfaces } from '@workbuddy/services/entities/entity.manifest';
import { registerManifest } from '@workbuddy/services/entities/entity.manifest.registry';
import { FILTER_PERIOD_PRESETS, FilterPeriodPreset } from '@workbuddy/ui/v2/blocks/filter';
import { clientCsvDownloadCommands } from '../shared/client.csv.download';
import { serviceHistoryColumns } from '../service.history/presentation/service.history.columns';
import { serviceHistoryCellRenderer } from '../service.history/presentation/service.history.result.cell.renderer';
import { describeWorkTaskScheduleSelection } from './bulk/work.task.schedule.visit.selection';
import { handleWorkTaskScheduleBulkCommand, workTaskScheduleBulkCommands } from './bulk/work.task.schedule.bulk.commands';
import { loadWorkTaskOccurrence, openWorkTaskOccurrence } from './surface/occurrence/work.task.occurrence.open';

const board: EntityManifest<WorkTaskScheduleRow>['board'] = {
  supportedDisplays: [EntityViewDisplay.Table],
  defaults: {
    // Requested here, not on board.panel, so the embedded Customer, Site and Service Plan tabs get it too.
    panel: {
      kind: 'quickview',
      width: 600,
      minWidth: 480,
      maxWidth: 900,
      ownsHeader: false,
      // The row is a projection: a forecast's id is a one-way hash no `get` can answer, so the pane gets the row.
      // Its surface reads the task's facts from the row's plan and dates.
      resolveTarget: ({ item, rowId }) => ({ entity: ActivityEntityName.WorkTaskSchedule, id: rowId, value: item }),
    },
    segmentTiles: true,
    // A read-only mount (a plan that is not Started, a locked record) drops selection and bulk on its own.
    itemInteraction: {
      selectable: {
        type: 'multiple',
        bulkCommands: workTaskScheduleBulkCommands,
      },
    },
    bulk: {
      onCommand: handleWorkTaskScheduleBulkCommand,
      describeSelection: describeWorkTaskScheduleSelection,
    },
    period: {
      field: 'date',
      presets: [...FILTER_PERIOD_PRESETS],
      defaultPreset: () => FilterPeriodPreset.ThisMonth,
      custom: true,
    },
    table: {
      fillerColumn: true,
      columns: {
        byField: {
          ...serviceHistoryColumns.byField,
          scheduleState: { minWidth: 150 },
          approvalStatus: { minWidth: 160 },
        },
      },
      overrideCellRenderer: serviceHistoryCellRenderer,
    },
    empty: {
      title: 'No matching task occurrences.',
      description: 'Choose a Customer, Site or Service Plan in the tenant app, then select a period.',
    },
    header: {
      title: 'Work',
      hideAdd: true,
      showSearch: true,
      showPaginator: true,
      showViewOptions: false,
    },
  },
};

/** The tenant's task panel; it reads a row and runs existing Visit commands, never a Work write. */
const surfaces: EntityManifestSurfaces<WorkTaskScheduleRow> = {
  [SurfaceType.Detail]: () => import('./surface/detail/work.task.detail.surface').then(module => module.workTaskDetailSurface),
};

/** The portal's one surface: the occurrence dialog, read-only, its record read by key. */
const portalSurfaces: EntityManifestSurfaces<WorkTaskScheduleRow> = {
  [SurfaceType.Detail]: () => import('./surface/occurrence/work.task.occurrence.surface').then(module => module.workTaskOccurrenceSurface),
};

// The same Download CSV as Services and Task history, from the same board request, so the file is the rows on screen.
const portalWorkCsvCommands = () => clientCsvDownloadCommands({ entity: ActivityEntityName.WorkTaskSchedule, path: clientPaths.WorkTaskSchedule, commandId: 'work-task-schedule.download-csv', title: 'Work' });

/** Preview only: a row is shown, never edited; the board's writes are the Visit bulk actions its tenant mounts run. */
export const workTaskScheduleManifest = registerManifest<WorkTaskScheduleRow>({
  entity: ActivityEntityName.WorkTaskSchedule,
  slug: 'work-task-schedule',
  routes: {
    root: `/${clientPaths.WorkTaskSchedule}`,
    record: () => null,
  },
  board,
  surfaces,
  operations: {
    // Reading a row the board already listed grants nothing extra; the actions check rights themselves. No model
    // swap: the factory reads each row's task in `initialize`, which runs once per mount, so a new row remounts.
    preview: { surface: SurfaceType.Detail },
  },
  domains: {
    [DomainType.ClientPortal]: {
      routes: {
        root: `/${clientPaths.WorkTaskSchedule}`,
        record: () => null,
      },
      // Domains replace the manifest wholesale. The dialog has no Work page behind it, so it is the inspector itself.
      surfaces: portalSurfaces,
      operations: {
        // Either Services right reads one occurrence, as on the Services board; the server rechecks both and the customer.
        preview: { surface: SurfaceType.Detail, loadRecord: loadWorkTaskOccurrence, authorize: ({ injector }) => injector.get(RightsService).hasRights(PortalClientSecurityRight.CanAccessServices) || injector.get(RightsService).hasRights(PortalClientSecurityRight.SiteServicesView) },
      },
      board: {
        ...board,
        defaults: {
          ...board.defaults,
          // Portal customers read their work; selection and every bulk action stay tenant-only.
          readonly: true,
          // A row opens the occurrence dialog, not the side panel; a row with no key opens nothing.
          panel: undefined,
          itemInteraction: {
            navigate: openWorkTaskOccurrence,
          },
          bulk: undefined,
          segmentTiles: false,
          shapeFilterFields: fields => fields.filter(field => !['customer', 'parent'].includes(field.name)),
          empty: {
            title: 'No matching task occurrences.',
          },
          header: {
            ...board?.defaults?.header,
            primaryCommands: portalWorkCsvCommands,
            // Readonly drops header commands as mutation entry points; Download CSV writes nothing.
            keepPrimaryCommandsWhenReadonly: true,
          },
        },
      },
    },
  },
});
